// src/theme.js
import React, { createContext, useMemo, useState } from 'react';
import { createTheme } from '@mui/material/styles';

/**
 * Equilibrium brand palette (from your image):
 *  - #6bf38d (mint)
 *  - #69946a (sage)
 *  - #51bc86 (emerald)
 *  - #28a36d (jade)
 *  - #2a5b2d (forest)
 *  - #000000 (black)
 *
 * Requirement: remove purples in DARK theme; keep blacks/greys. To make the change
 * obvious and consistent, this version uses the Equilibrium greens as the PRIMARY
 * palette in BOTH light and dark modes. (No purple anywhere.)
 */

export const tokens = (mode) => {
  const grey = {
    100: '#e0e0e0',
    200: '#c2c2c2',
    300: '#a3a3a3',
    400: '#858585',
    500: '#666666',
    600: '#525252',
    700: '#3d3d3d',
    800: '#292929',
    900: '#141414',
  };

  // Exact brand colors from the swatch
  const brand = {
    mint:   '#6bf38d',
    sage:   '#69946a',
    emerald:'#51bc86',
    jade:   '#28a36d',
    forest: '#2a5b2d',
    black:  '#000000',
  };

  // Keep these around for alerts/badges etc. (no purple use)
  const blueAccent = {
    100: '#e1e8ff', 200: '#c3ccff', 300: '#a4afff', 400: '#8693ff', 500: '#5588FF',
    600: '#3366FF', 700: '#1144CC', 800: '#0a339b', 900: '#082a7a'
  };
  const greenAccent = {
    100: '#d1fae5', 200: '#a7f3d0', 300: '#6ee7b7', 400: '#34d399', 500: '#10B981',
    600: '#059669', 700: '#047857', 800: '#065f46', 900: '#064e3b'
  };
  const redAccent = {
    100: '#fee2e2', 200: '#fecaca', 300: '#fca5a5', 400: '#f87171', 500: '#EF4444',
    600: '#dc2626', 700: '#b91c1c', 800: '#991b1b', 900: '#7f1d1d'
  };

  return {
    grey,
    brand,        // Equilibrium greens (source of truth)
    blueAccent,
    greenAccent,
    redAccent,

    // Back-compat if something in the app still reads tokens.primary/secondary
    primary: brand,
    secondary: { main: brand.jade, light: brand.mint, dark: brand.forest },
  };
};

export const themeSettings = (mode) => {
  const colors = tokens(mode);
  const isDark = mode === 'dark';

  // Primary palette built from the Equilibrium greens
  const primary = {
    light: colors.brand.mint,
    main:  colors.brand.emerald, // #51bc86
    dark:  colors.brand.forest,  // #2a5b2d
    contrastText: '#ffffff',
  };

  // Secondary as a complementary green tone (muted)
  const secondary = {
    light: colors.brand.jade,    // #28a36d
    main:  colors.brand.sage,    // #69946a
    dark:  colors.brand.forest,  // #2a5b2d
    contrastText: '#ffffff',
  };

  // Light-only subtle gradient for things like side drawers (no purple)
  const sidebarGradient = `linear-gradient(180deg, ${colors.brand.mint} 0%, ${colors.brand.emerald} 100%)`;

  return createTheme({
    palette: {
      mode,
      primary,
      secondary,
      background: {
        default: isDark ? colors.grey[900] : '#FFFFFF',
        paper:   isDark ? colors.grey[800] : '#F7F7F7',
      },
      // Status colors
      error:   { main: colors.redAccent[500] },
      warning: { main: colors.redAccent[400] },
      info:    { main: colors.blueAccent[500] },
      success: { main: colors.brand.emerald },
      // Text colors in dark stay grey-based (requirement)
      text: isDark
        ? { primary: '#f3f3f3', secondary: colors.grey[300] }
        : { primary: '#111111', secondary: colors.grey[600] },
    },

    typography: {
      fontFamily: ['Inter', 'system-ui', 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', 'sans-serif'].join(','),
      fontSize: 14,
      h1: { fontSize: '2.5rem', fontWeight: 700, lineHeight: 1.2 },
      h2: { fontSize: '2rem',   fontWeight: 600, lineHeight: 1.25 },
      h3: { fontSize: '1.75rem',fontWeight: 600, lineHeight: 1.3 },
      body1: { fontSize: '1rem' },
      body2: { fontSize: '0.875rem' },
    },

    shape: { borderRadius: 8 },

    components: {
      MuiDrawer: {
        styleOverrides: {
          paper: {
            background: isDark ? colors.grey[900] : sidebarGradient,
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          colorPrimary: {
            backgroundColor: isDark ? colors.grey[900] : colors.brand.emerald,
            color: isDark ? '#fff' : '#fff',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: { textTransform: 'none', borderRadius: 8 },
          containedPrimary: {
            boxShadow: 'none',
            ':hover': { boxShadow: 'none' },
          },
        },
      },
      MuiLink: {
        styleOverrides: {
          root: {
            color: colors.brand.emerald,
            '&:hover': { color: colors.brand.jade },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: isDark ? colors.grey[600] : colors.grey[300],
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: colors.brand.emerald,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: colors.brand.emerald,
            },
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            color: colors.brand.sage,
            '&.Mui-checked': { color: colors.brand.emerald },
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          switchBase: {
            '&.Mui-checked': {
              color: colors.brand.emerald,
              '& + .MuiSwitch-track': { backgroundColor: colors.brand.emerald },
            },
          },
        },
      },
    },
  });
};

// Color mode provider
export const colorModeContext = createContext({ toggleColorMode: () => {} });

export const useMode = () => {
  const [mode, setMode] = useState('light');
  const colorMode = useMemo(
    () => ({ toggleColorMode: () => setMode((prev) => (prev === 'light' ? 'dark' : 'light')) }),
    []
  );
  const theme = useMemo(() => themeSettings(mode), [mode]);
  return [theme, colorMode];
};
